import { onScopeDispose, watch } from 'vue'
import type { Ref } from 'vue'

import type { Service } from '#ourahub-intra/types/services'

/** Renders the same MDI font glyph used by service cards without loading another icon library. */
export function useServiceFavicon(service: Ref<Service | undefined>, color: Ref<string>) {
  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')

  if (!favicon) return

  const originalHref = favicon.href
  const originalType = favicon.type

  function reset() {
    favicon!.href = originalHref
    favicon!.type = originalType
  }

  watch(
    [() => service.value?.icon, color],
    async ([icon, _iconColor], _previous, onCleanup) => {
      let cancelled = false

      onCleanup(() => {
        cancelled = true
      })

      reset()

      if (!icon || !/^mdi-[a-z0-9-]+$/.test(icon)) return

      const element = document.createElement('i')
      element.className = `mdi ${icon}`
      element.style.display = 'none'
      document.body.append(element)

      const style = getComputedStyle(element, '::before')
      const glyph = style.content.replace(/^['"]|['"]$/g, '')
      const font = `56px ${style.fontFamily}`

      element.remove()

      if (!glyph || glyph === 'none' || glyph === 'normal') return

      try {
        await document.fonts.load(font, glyph)

        if (cancelled) return

        const canvas = document.createElement('canvas')
        canvas.width = 64
        canvas.height = 64

        const context = canvas.getContext('2d')

        if (!context) return

        context.fillStyle = '#2F4A73'
        context.beginPath()
        context.arc(32, 32, 32, 0, Math.PI * 2)
        context.fill()

        context.font = font
        context.fillStyle = 'white'
        context.textAlign = 'center'
        context.textBaseline = 'middle'
        context.fillText(glyph, 32, 32)

        favicon.type = 'image/png'
        favicon.href = canvas.toDataURL('image/png')
      } catch {
        if (!cancelled) reset()
      }
    },
    { immediate: true },
  )

  onScopeDispose(reset)
}
